import { useToast, UseToastOptions } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  FieldValues,
  useForm,
  UseFormProps,
  UseFormReturn,
} from "react-hook-form";

import { translateError } from "@/common/utils/errorHandling";
import { ErrorResponse } from "@reflag/shared/api";
import { z } from "@reflag/shared/utils/zod";

import { useErrorToast } from "./useErrorToast";

type CustomErrorToastTitle =
  | string
  | ((error: AxiosError<ErrorResponse>) => string)
  | undefined;

type TranslatedError = ReturnType<typeof translateError>;

type CustomErrorToastOptions =
  | UseToastOptions
  | ((
      error: AxiosError<ErrorResponse>,
      translatedError: TranslatedError,
    ) => UseToastOptions);

type FormMutationSubmitHandlerOptions<TFormFields, TVariables, TResult> = {
  prepareVariables?: (data: TFormFields) => Promise<TVariables> | TVariables;
  successToast?: string | ((result: TResult) => string);
  errorToast?: CustomErrorToastTitle;
  errorToastOptions?: CustomErrorToastOptions;
};

function handleFormErrors<TFormFields extends FieldValues>(
  form: UseFormReturn<TFormFields>,
  error: AxiosError<ErrorResponse>,
  toast?: {
    errorToast: (props?: UseToastOptions) => void;
    customErrorToastTitle?: CustomErrorToastTitle;
    customErrorToastOptions?: CustomErrorToastOptions;
  },
) {
  const translatedError = translateError(error, toast?.customErrorToastTitle);
  const { title, description, fieldErrors, status } = translatedError;

  if (status === 400) {
    const hasFieldErrors = Object.keys(fieldErrors).length > 0;
    if (hasFieldErrors) {
      Object.entries(fieldErrors).forEach(([fieldName, error]) => {
        form.setError(fieldName as Parameters<typeof form.setError>[0], error);
      });
    } else {
      form.setError("root", { message: description });
    }
  } else if (toast) {
    const customToastOptions =
      typeof toast.customErrorToastOptions === "function"
        ? toast.customErrorToastOptions(error, translatedError)
        : toast.customErrorToastOptions;

    toast.errorToast({
      title,
      description,
      ...customToastOptions,
    });
  }
}

export default function useApiForm<TArgs extends FieldValues, TResult>(
  apiHandler: (data: TArgs) => Promise<TResult>,
  argsSchema: z.ZodType<TArgs, TArgs>,
  mutationOptions?: Omit<
    UseMutationOptions<TResult, AxiosError, TArgs>,
    "mutationFn"
  >,
  formOptions?: UseFormProps<TArgs>,
) {
  // form
  const form = useForm<TArgs>({
    resolver: zodResolver(argsSchema),
    mode: "onChange",
    ...formOptions,
  });

  // mutation
  const mutation = useMutation<TResult, AxiosError<ErrorResponse>, TArgs>({
    mutationFn: apiHandler,
    retry: 0,
    ...mutationOptions,
  });

  const { mutateAsync } = mutation;

  const handleSubmit = form.handleSubmit((data) =>
    mutateAsync(data)
      .then(() => {
        form.clearErrors();
      })
      .catch((err: AxiosError<ErrorResponse>) => {
        form.clearErrors();

        handleFormErrors(form, err);
      }),
  );

  return {
    form,
    handleSubmit,
    mutation,
  };
}

export function useFormMutationSubmitHandler<
  TFormFields extends FieldValues,
  TValue,
  TVariables = TFormFields,
>(
  form: UseFormReturn<TFormFields>,
  mutation: UseMutationResult<TValue, AxiosError<ErrorResponse>, TVariables>,
  onSuccess?: (result: TValue, variables: TFormFields) => void,
  options?: FormMutationSubmitHandlerOptions<TFormFields, TVariables, TValue>,
) {
  const toast = useToast();
  const errorToast = useErrorToast();

  return form.handleSubmit(async (data) => {
    form.clearErrors();

    let variables: TVariables;
    try {
      variables = options?.prepareVariables
        ? await options.prepareVariables(data)
        : (data as unknown as TVariables);
    } catch (error) {
      form.setError("root", {
        message: error instanceof Error ? error.message : "Invalid form data",
      });
      return;
    }

    let result: TValue;
    try {
      result = await mutation.mutateAsync(variables);
    } catch (error) {
      form.reset(undefined, {
        keepDirty: true,
        keepValues: true,
      });

      handleFormErrors(form, error as AxiosError<ErrorResponse>, {
        errorToast,
        customErrorToastTitle: options?.errorToast,
        customErrorToastOptions: options?.errorToastOptions,
      });
      return;
    }

    const title =
      typeof options?.successToast === "function"
        ? options?.successToast(result)
        : options?.successToast;

    if (title) {
      toast({
        title,
        status: "success",
      });
    }
    onSuccess?.(result, data);
  });
}
